import React, { useMemo, useState } from 'react';
import { CheckCircle2Icon, ChevronRightIcon, CopyIcon, Loader2Icon, XIcon } from 'lucide-react';
import { Button } from './components/ui/Button';
import { Field } from './components/ui/Field';
import { Select } from './components/ui/Select';
import { LoanPreviewTable } from './components/LoanPreviewTable';
import { employees, loans as allLoans } from './data/commissions';
import { effectiveBps, formatDate, formatSigned, loansInRange, recalculate } from './utils/commissions';
export function App() {
  const [employeeId, setEmployeeId] = useState(employees[0].id);
  const [bps, setBps] = useState('');
  const [shortPct, setShortPct] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [fundedFrom, setFundedFrom] = useState('');
  const [fundedTo, setFundedTo] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const employee = employees.find(e => e.id === employeeId)!;
  const errors = useMemo(() => {
    const next: Record<string, string> = {};
    const bpsNum = Number(bps);
    const shortNum = Number(shortPct);
    if (bps === '' || Number.isNaN(bpsNum)) next.bps = 'Enter a bps rate.';else if (bpsNum < 0 || bpsNum > 500) next.bps = 'Must be between 0 and 500 bps.';
    if (shortPct === '' || Number.isNaN(shortNum)) next.shortPct = 'Enter a short-loan percentage.';else if (shortNum < 0 || shortNum > 100) next.shortPct = 'Must be between 0 and 100.';
    if (!startDate) next.startDate = 'An effective start date is required.';
    if (startDate && endDate && endDate < startDate) next.endDate = 'End date must fall on or after the start date.';
    if (fundedFrom && fundedTo && fundedTo < fundedFrom) next.fundedTo = 'End of range must fall on or after the start.';
    return next;
  }, [bps, shortPct, startDate, endDate, fundedFrom, fundedTo]);
  const rateReady = bps !== '' && shortPct !== '' && !errors.bps && !errors.shortPct;
  const employeeLoans = useMemo(() => allLoans.filter(loan => loan.employeeId === employeeId), [employeeId]);
  const selected = useMemo(() => errors.fundedTo ? [] : loansInRange(employeeLoans, fundedFrom, fundedTo), [employeeLoans, fundedFrom, fundedTo, errors.fundedTo]);
  const previewLoans = useMemo(() => rateReady ? recalculate(selected, Number(bps), Number(shortPct)) : [], [selected, rateReady, bps, shortPct]);
  const net = previewLoans.reduce((sum, loan) => sum + loan.difference, 0);
  const visible = showErrors ? errors : {};
  const matchEffectiveDates = () => {
    setFundedFrom(startDate);
    setFundedTo(endDate);
  };
  const handleSave = () => {
    if (Object.keys(errors).length > 0) {
      setShowErrors(true);
      return;
    }
    setSaving(true);
    window.setTimeout(() => {
      setSaving(false);
      setConfirmation(`Comp plan saved for ${employee.name} — ${previewLoans.length} loan${previewLoans.length === 1 ? '' : 's'} recalculated, net ${formatSigned(net)}.`);
    }, 650);
  };
  const shortBps = rateReady ? effectiveBps(Number(bps), Number(shortPct), 'short') : 0;
  return <div className="min-h-full w-full bg-canvas font-sans" data-id="element-0" data-mp="13ey35vqwpa9y">
      <header className="mx-auto w-full max-w-[980px] px-4 pb-1 pt-6 lg:px-8" data-id="element-1" data-mp="1r3zebqr4izix">
        <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-2" data-mp="1w33sywwthpgn">
          <div data-id="element-3" data-mp="1w0zmc77jacco">
            <h1 className="text-2xl font-extrabold leading-tight tracking-tight text-ink-900" data-id="element-4" data-mp="1i3ukykirm1nr">
              Update Comp Plan
            </h1>
            <p className="mt-1 text-[13px] text-ink-500" data-id="element-5" data-mp="1m3uwnxehbyr4">
              Set a commission rate and recalculate loans funded in a date range.
            </p>
          </div>
          <span className="mb-1 rounded-full border border-leaf-200 bg-leaf-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-leaf-700" data-id="element-6" data-mp="aqitzpughm6j">
            Commission Admin
          </span>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[980px] px-4 py-5 lg:px-8" data-id="element-7" data-mp="m4uid13epckk">
        {confirmation && <div role="status" className="mb-4 flex items-start gap-2.5 rounded border border-success-border bg-success-bg px-4 py-3" data-id="element-8" data-mp="h7vliascyrio">
            <CheckCircle2Icon className="mt-px h-4 w-4 shrink-0 text-success-fg" aria-hidden="true" data-id="element-9" data-mp="19w9ol4yb0e13" />
            <p className="flex-1 text-[13px] leading-snug text-ink-700" data-id="element-10" data-mp="14uu1l7ckfxg2">{confirmation}</p>
            <button type="button" onClick={() => setConfirmation(null)} aria-label="Dismiss confirmation" className="-mr-1 -mt-0.5 rounded p-1 text-success-fg transition-colors duration-150 ease-out hover:bg-white/70" data-id="element-11" data-mp="zw0mxbljm0eb">
              <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-12" data-mp="3gkwcorx2i1cj" />
            </button>
          </div>}

        <div className="space-y-4" data-id="element-13" data-mp="1w69pe54hp9sq">
          <form className="rounded-md border border-surface-line bg-surface shadow-card" onSubmit={event => {
          event.preventDefault();
          handleSave();
        }} data-id="element-14" data-mp="205qw11vnkczh">
            <div className="grid grid-cols-1 gap-5 px-5 py-5 md:grid-cols-2" data-id="element-15" data-mp="29wva7zdi7tt9">
              <Select id="employee" label="Employee" required value={employeeId} onChange={value => {
              setEmployeeId(value);
              setConfirmation(null);
            }} options={employees.map(e => ({
              value: e.id,
              label: `${e.name} — ${e.role}`
            }))} hint={`${employee.team} · currently ${employee.currentBps} bps, ${employee.currentShortPct}% on short loans`} data-id="element-16" data-mp="2evfz52nb2r2e" />

              <div className="grid grid-cols-2 gap-4" data-id="element-17" data-mp="yihob02b9ofv">
                <Field id="bps" label="Commission rate" required type="number" min="0" max="500" suffix="bps" placeholder="125" value={bps} error={visible.bps} onChange={setBps} data-id="element-18" data-mp="21sdfy6d1lvia" />
                <Field id="short-pct" label="Short loans" required type="number" min="0" max="100" suffix="%" placeholder="60" value={shortPct} error={visible.shortPct} hint={rateReady ? `= ${shortBps.toFixed(0)} bps on short loans` : '% of the full rate'} onChange={setShortPct} data-id="element-19" data-mp="2kpeo201w8bqg" />
              </div>

              <fieldset className="border-t border-surface-line pt-4" data-id="element-20" data-mp="2yfqgk02e4ci4">
                <legend className="text-[11px] font-semibold uppercase tracking-wider text-ink-500" data-id="element-21" data-mp="3i4xzemyh20wu">
                  Effective dates
                </legend>
                <div className="mt-3 grid grid-cols-2 gap-4" data-id="element-22" data-mp="385p6zyj98ucl">
                  <Field id="start-date" label="Start date" required type="date" value={startDate} error={visible.startDate} onChange={setStartDate} data-id="element-23" data-mp="387jkwsw3zm76" />
                  <Field id="end-date" label="End date" type="date" value={endDate} error={visible.endDate} hint="Blank = stays active" onChange={setEndDate} data-id="element-24" data-mp="345ol5stttgvi" />
                </div>
              </fieldset>

              <fieldset className="border-t border-surface-line pt-4" data-id="element-25" data-mp="2kekz70ndqdpx">
                <div className="flex items-baseline justify-between gap-3" data-id="element-26" data-mp="30vnbm1k4pece">
                  <legend className="text-[11px] font-semibold uppercase tracking-wider text-ink-500" data-id="element-27" data-mp="2vwamtb9p5x76">
                    Loans to recalculate
                  </legend>
                  <Button variant="neutral" onClick={matchEffectiveDates} disabled={!startDate} title="Copy the effective start and end dates into the funded-date range" className="h-7 px-2.5 text-[12px]" data-id="element-28" data-mp="2qzajh848f8jr">
                    <CopyIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-29" data-mp="286pva3zh9rma" />
                    Match effective dates
                  </Button>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-4" data-id="element-30" data-mp="239g8kw3h3u25">
                  <Field id="funded-from" label="Funded from" type="date" value={fundedFrom} onChange={setFundedFrom} data-id="element-31" data-mp="1y8s71ghmbuto" />
                  <Field id="funded-to" label="Funded to" type="date" value={fundedTo} error={visible.fundedTo} onChange={setFundedTo} data-id="element-32" data-mp="9ompar5ku5hw" />
                </div>
                <p className="mt-2 text-[11px] leading-snug text-ink-500" data-id="element-33" data-mp="nnw3jla82pj8">
                  Leave both blank to include every funded loan for this employee.
                </p>
              </fieldset>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-surface-line bg-surface-deep px-5 py-3.5" data-id="element-34" data-mp="3r31jamqiw5sv">
              <Button variant="neutral" onClick={() => {
              setBps('');
              setShortPct('');
              setStartDate('');
              setEndDate('');
              setFundedFrom('');
              setFundedTo('');
              setShowErrors(false);
              setConfirmation(null);
            }} data-id="element-35" data-mp="4pjkn7tie1ag">
                Cancel
              </Button>
              <Button variant="brand" onClick={handleSave} disabled={saving} className="h-9" data-id="element-36" data-mp="2k4nae3b53eb2">
                {saving ? <>
                    <Loader2Icon className="h-3.5 w-3.5 animate-spin" aria-hidden="true" data-id="element-37" data-mp="2u21npu1rnmz5" />
                    Saving…
                  </> : 'Save & Recalculate'}
              </Button>
            </div>
          </form>

          <section className="rounded-md border border-surface-line bg-surface shadow-card" data-id="element-38" data-mp="26diffeipva72">
            <h2 data-id="element-39" data-mp="2b7342fyf6rtl">
              <button type="button" onClick={() => setPreviewOpen(prev => !prev)} aria-expanded={previewOpen} aria-controls="loans-in-range-panel" className="flex w-full items-center gap-3 rounded px-5 py-3.5 text-left transition-colors duration-150 ease-out hover:bg-surface-deep" data-id="element-40" data-mp="2b7mp45jd9wbu">
                <ChevronRightIcon className={`h-4 w-4 shrink-0 text-ink-500 transition-transform duration-150 ease-out ${previewOpen ? 'rotate-90' : ''}`} aria-hidden="true" data-id="element-41" data-mp="3ryf1rbprvxll" />
                <span className="flex-1" data-id="element-42" data-mp="3d0wdeah0bmeo">
                  <span className="block text-[15px] font-semibold text-ink-900" data-id="element-43" data-mp="3d2r27zvefyo1">
                    Loans in range
                  </span>
                  <span className="mt-0.5 block text-xs text-ink-500" data-id="element-44" data-mp="347t50gl7xzqw">
                    {employee.name} ·{' '}
                    {fundedFrom || fundedTo ? `funded ${fundedFrom ? formatDate(fundedFrom) : 'any time'} – ${fundedTo ? formatDate(fundedTo) : 'today'}` : 'all funded loans'}
                  </span>
                </span>
                {rateReady && previewLoans.length > 0 && <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[12px] font-semibold tabular-nums ${net >= 0 ? 'border-success-border bg-success-bg text-success-fg' : 'border-danger-border bg-danger-bg text-danger-fg'}`} data-id="element-45" data-mp="2km9ckjzachmo">
                    {previewLoans.length} loans · {formatSigned(net)}
                  </span>}
              </button>
            </h2>

            {previewOpen && <div id="loans-in-range-panel" className="border-t border-surface-line" data-id="element-46" data-mp="2pldo3h9omrg6">
                <LoanPreviewTable loans={previewLoans} ready={rateReady} data-id="element-47" data-mp="2tlo47mr6uy8r" />
              </div>}
          </section>
        </div>
      </main>
    </div>;
}